import { StandardEvents } from '@shopify/events';

/**
 * Atelier Nº7 — variantes.
 * 1. <n7-variant-sync> : remplace son contenu par celui de la section re-rendue
 *    quand Horizon change de variante (métachamps de variante : livraison, délai, dimensions…).
 * 2. Sélecteur N7 (.n7-picker) : nom de la valeur dans le libellé au survol / au choix,
 *    aperçu de l'image de la variante dans la galerie au survol (souris uniquement).
 * 3. <n7-card-swatches> : mini-pastilles des cartes produit (image + lien de la variante).
 */

class N7VariantSync extends HTMLElement {
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const fresh = html.querySelector(`n7-variant-sync[data-sync-key="${this.dataset.syncKey}"]`);
        if (fresh && fresh.innerHTML !== this.innerHTML) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

if (!customElements.get('n7-variant-sync')) customElements.define('n7-variant-sync', N7VariantSync);

/* ---------- Sélecteur de la fiche produit ---------- */

const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

/** @param {Element} label */
function legendValue(label) {
  return label.closest('fieldset')?.querySelector('.n7-vopt__value') ?? null;
}

/** Image principale visible de la galerie de la même section. */
function mainGalleryImage(picker) {
  const scope = picker.closest('.shopify-section, dialog') ?? document;
  const gallery = scope.querySelector('media-gallery');
  if (!gallery) return null;
  const slide = gallery.querySelector('slideshow-slide[aria-hidden="false"], slideshow-slide:not([aria-hidden="true"])');
  return (slide ?? gallery).querySelector('img');
}

let preview = null; // { img, src, srcset, committed }

function startPreview(label) {
  const picker = label.closest('.n7-picker');
  if (!picker?.dataset.n7HoverPreview || !label.dataset.n7Preview) return;
  const img = mainGalleryImage(picker);
  if (!img) return;
  if (!preview || preview.img !== img) {
    endPreview();
    preview = { img, src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), committed: false };
  }
  img.srcset = label.dataset.n7PreviewSrcset || '';
  img.src = label.dataset.n7Preview;
}

function endPreview() {
  if (!preview) return;
  const { img, src, srcset, committed } = preview;
  preview = null;
  if (committed || !img.isConnected) return;
  if (srcset) img.setAttribute('srcset', srcset);
  else img.removeAttribute('srcset');
  if (src) img.setAttribute('src', src);
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || !canHover.matches) return;
  const label = event.target instanceof Element ? event.target.closest('.n7-vopt__item--swatch') : null;
  if (!label || label.contains(event.relatedTarget)) return;
  const value = legendValue(label);
  if (value) value.textContent = label.dataset.n7Value ?? '';
  if (!label.classList.contains('is-unavailable')) startPreview(label);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse') return;
  const label = event.target instanceof Element ? event.target.closest('.n7-vopt__item--swatch') : null;
  if (!label || label.contains(event.relatedTarget)) return;
  const value = legendValue(label);
  if (value) value.textContent = value.dataset.n7Selected ?? '';
  endPreview();
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.closest('.n7-picker')) return;
  const label = input.closest('.n7-vopt__item');
  const value = label && legendValue(label);
  if (value) {
    value.dataset.n7Selected = label.dataset.n7Value ?? '';
    value.textContent = value.dataset.n7Selected;
  }
  // La galerie va être remplacée par celle de la variante choisie : on garde l'aperçu jusque-là.
  if (preview) preview.committed = true;
});

/* ---------- Préchargement léger des photos principales des variantes ---------- */
// La galerie est remplacée par celle de la variante choisie (rendu de section Horizon) : on précharge,
// au premier contact avec le sélecteur et à faible priorité, la seule photo principale de chaque autre variante,
// à la largeur que la galerie choisira (mêmes srcset/sizes que la 1re image) pour que le cache serve.
const preloaded = new Set();

function preloadHeroes() {
  const connection = /** @type {any} */ (navigator).connection;
  if (connection?.saveData || /2g/.test(connection?.effectiveType ?? '')) return;
  for (const data of document.querySelectorAll('media-gallery script[data-n7-heroes]')) {
    const main = data.closest('media-gallery')?.querySelector('img[srcset]');
    if (!main) continue;
    let heroes = [];
    try {
      heroes = JSON.parse(data.textContent || '[]');
    } catch {
      continue;
    }
    const widths = main.srcset
      .split(',')
      .map((candidate) => candidate.trim().split(/\s+/))
      .filter(([url, w]) => url && w)
      .map(([url, w]) => [new URL(url, location.href).searchParams.get('width'), w]);
    for (const hero of heroes) {
      if (preloaded.has(hero)) continue;
      preloaded.add(hero);
      const img = new Image();
      img.fetchPriority = 'low';
      img.decoding = 'async';
      img.sizes = main.sizes;
      img.srcset = widths.map(([width, w]) => `${hero}${hero.includes('?') ? '&' : '?'}width=${width} ${w}`).join(', ');
    }
  }
}

// Round 13 (audit #1, poids mobile) : plus de préchargement automatique de toutes les variantes (24 photos sur
// Apollo). Il démarre au premier contact avec le sélecteur (survol, toucher ou clavier), juste avant le choix.
const PICKER = 'variant-picker, .n7-vopt';
function preloadOnIntent(event) {
  if (!(event.target instanceof Element) || !event.target.closest(PICKER)) return;
  for (const type of ['pointerover', 'pointerdown', 'focusin']) document.removeEventListener(type, preloadOnIntent, true);
  preloadHeroes();
}
for (const type of ['pointerover', 'pointerdown', 'focusin']) document.addEventListener(type, preloadOnIntent, true);

/* ---------- Mini-pastilles des cartes produit ---------- */

class N7CardSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.n7-cs__btn') : null;
    if (!button) return;
    event.preventDefault();

    for (const other of this.querySelectorAll('.n7-cs__btn')) {
      other.setAttribute('aria-pressed', String(other === button));
    }

    const card = this.closest('product-card') ?? this.parentElement;
    if (!card) return;

    // Image de la carte, puis 2e photo (affichée au survol) : celles de la variante choisie uniquement
    const images = Array.from(card.querySelectorAll('img')).filter((el) => !el.closest('n7-card-swatches'));
    const setImage = (img, src, srcset) => {
      if (!img || !src) return;
      img.removeAttribute('srcset');
      if (srcset) img.srcset = srcset;
      img.src = src;
    };
    setImage(images[0], button.dataset.img, button.dataset.srcset);
    // QC-01 : packshot en « contain », photo d'ambiance ou recadrage en « cover » (sans bandes)
    if (button.dataset.fit) card.querySelector('.card-gallery')?.classList.toggle('n7-cover', button.dataset.fit === 'cover');
    if (images[1]?.closest('slideshow-slide:not([hidden])')) {
      // sans 2e photo, le survol garde la photo principale de la variante
      setImage(images[1], button.dataset.img2 || button.dataset.img, button.dataset.img2 ? button.dataset.srcset2 : button.dataset.srcset);
    }

    // Liens de la carte vers la variante choisie
    const productPath = new URL(this.dataset.productUrl ?? '', window.location.origin).pathname;
    for (const link of card.querySelectorAll('a[href]')) {
      if (link.closest('n7-card-swatches')) continue;
      const url = new URL(link.href, window.location.origin);
      if (url.pathname !== productPath) continue;
      url.searchParams.set('variant', button.dataset.variantId ?? '');
      link.href = url.pathname + url.search + url.hash;
    }
  };
}

if (!customElements.get('n7-card-swatches')) customElements.define('n7-card-swatches', N7CardSwatches);
